import _ from 'lodash';
import manifest from '../../manifest.json';

// Convenience getters for extracting data in a standardized format
// Creates a function that can be applied to a row to extract a particular value
// Will return null if the property is missing or empty string
const get = (prop, defaultValue, transform = v => v) => obj => {
    if (obj[prop] === undefined || obj[prop] === '') {
        if (defaultValue !== undefined) {
            return defaultValue;
        }
        return null;
    }

    return transform(obj[prop]);
};
const getAsCamelCasedString = (prop, defaultValue) => get(prop, defaultValue, v => _.camelCase(v));
const getAsNumber = (prop, defaultValue) => get(prop, defaultValue, v => +v);
const getAsInt = (prop, defaultValue) => get(prop, defaultValue, v => parseInt(v, 10));
const getAsBool = (prop, defaultValue) => get(prop, defaultValue, v => _.lowerCase(v) !== 'false' && +v !== 0 && !!v);
const constant = value => () => value;

export default {
    processFile: ({ columns }) => row => _.mapValues(columns, fn => fn(row)),
    inputFileConfig: {
        postalLookup: {
            file: manifest.data.postalLookup
        },
        lgaClassLookup: {
            file: manifest.data.lgaClassLookup
        },
        lgaGeojson: {
            file: manifest.data.lgaGeojson
        },
        appConfig: {
            file: manifest.appConfig,
        },
        metricsConfig: {
            file: manifest.metricsConfig,
        },
        metrics: {
            file: manifest.data.metrics,
            columns: {
                metric: get('id'),
                label: get('vizLabel'),
                section: get('section'),
                chartType: get('chartType', 'waffle'),
                isPrimary: getAsBool('isPrimary'),
                sortIndex: getAsNumber('sortIndex'),
                positiveDirection: getAsNumber('positiveDirection'),
                units: get('units', ''),
                formatter: get('formatter'),
                domain: get('domain', ''),
                showZoneData: getAsBool('showZoneData'),
                definition: get('vizTooltipDefinition', ''),
                linkToHealth: get('vizTooltipLinkToHealth', ''),
                year: get('year', ''),
                dataSource: get('dataSource', ''),
                keyColumn: getAsCamelCasedString('keyColumn'),
                pdfLabel: get('pdfLabel'),
                shortLabel: get('shortLabel'),
                pdfDescription: get('pdfDescription'),
                target: getAsNumber('target'),
                exclude: getAsBool('exclude'),
            }
        },
        municipalities: {
            file: manifest.data.municipalities,
            columns: {
                name: get('name'),
                lng: row => +get('longitude')(row),
                lat: row => +get('latitude')(row),
                lga: get('lga'),
                labelPosition: get('label')
            }
        },
        metricValues: {
            file: manifest.data.metricValues,
            columns: {
                metric: get('metric'),
                value: getAsNumber('value'),
                geographyCode: get('geographyCode')
            }
        },
        education: {
            file: manifest.data.education,
            columns: {
                metric: get('metric'),
                value: getAsNumber('value'),
                educationLevel: get('educationLevel'),
                geographyCode: get('geographyCode')
            }
        },
        cdi: {
            file: manifest.data.cdi,
            columns: {
                metric: get('metric'),
                value: getAsNumber('value'),
                geographyCode: get('geographyCode'),
                cdiCategory: get('cdiCategory'),
            }
        },
        immigration: {
            file: manifest.data.immigration,
            columns: {
                metric: get('metric'),
                value: getAsNumber('value'),
                geographyCode: get('geographyCode'),
                immigrationLabel: get('immigrationLabel')
            }
        },
        populationGrowth: {
            file: manifest.data.populationGrowth,
            columns: {
                metric: constant('populationGrowth'),
                section: constant('demographics'),
                geographyCode: get('geographyCode'),
                year: getAsNumber('year'),
                value: getAsNumber('population')
            }
        },
        populationAgeGender: {
            file: manifest.data.populationAgeGender,
            columns: {
                metric: constant('populationAgeGender'),
                section: constant('demographics'),
                geographyCode: get('geographyCode'),
                sex: get('sex'),
                age: getAsInt('age'),
                value: getAsNumber('population')
            }
        }
    }
};
